/* eslint-disable max-params */
/* eslint-disable @typescript-eslint/naming-convention */
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  Signal,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { MatTabsModule } from '@angular/material/tabs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatBadgeModule } from '@angular/material/badge';

import {
  DialogSize,
  WcgButtonComponent,
  WcgDialogComponent,
  WcgInputHeight,
  WcgMenuOption,
  WcgUniSelectComponent,
} from '@wcg/frontend-framework';

import {
  AddDocumentTypeDialogData,
  DocumentTypesFormArray,
} from '@data-access/configurations/types/submissions.type';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    WcgDialogComponent,
    MatTabsModule,
    NgTemplateOutlet,
    WcgButtonComponent,
    WcgUniSelectComponent,
    MatBadgeModule,
    MatProgressSpinner,
  ],
  selector: 'wcg-add-document-type-dialog',
  styleUrls: ['./document-type-dialog.component.css'],
  template: `
    <form [formGroup]="documentTypeDialogForm" novalidate>
      <wcg-dialog [title]="$title()" [size]="size.MEDIUM">
        <div class="w-full flex p-4 submission-type-dialog__container" content>
          @defer (
            when data.$documentTypes() && data.$attributes()  && $documentFormGroup()
          ) {
            <div class="w-full flex flex-col gap-8 mb-8">
              <div class="w-full">
                @if (data.$mode() === 'Edit') {
                  <div class="document-type-dialog__label">
                    <strong>Document Type:</strong>
                    {{
                      documentTypeDialogForm.controls.documentType.value?.label
                    }}
                  </div>
                } @else {
                  <ng-container
                    [ngTemplateOutlet]="selectRef"
                    [ngTemplateOutletContext]="{
                      formControl: documentTypeDialogForm.controls.documentType,
                      options: $documentTypesList(),
                      props: {
                        required: true,
                        label: 'Document Type',
                        errorText:
                          $errorText().length > 0
                            ? $errorText()
                            : 'DocumentType is required',
                      },
                    }"
                  />
                }
              </div>
              <div class="w-full mt-4">
                <ng-container
                  [ngTemplateOutlet]="selectRef"
                  [ngTemplateOutletContext]="{
                    formControl: documentTypeDialogForm.controls.attribute,
                    options: $attributesList(),
                    props: {
                      required: true,
                      label: 'Attribute',
                      errorText: 'Attribute is required',
                    },
                  }"
                />
              </div>
            </div>
          } @placeholder {
            <p>Nothing is happening</p>
          }
        </div>
        <!------------/Dialog Content Container------------>

        <!------------------Dialog Footer----------------->
        <div tertiary>
          <wcg-button
            color="tertiary"
            data-testid="delete-dialog__cancel"
            (click)="cancel()"
          >
            Cancel
          </wcg-button>
        </div>

        <div primary>
          <wcg-button
            data-testid="delete-dialog__confirm"
            color="primary"
            (click)="save()"
            [isDisabled]="
              documentTypeDialogForm.invalid || $requestInProgress()
            "
          >
            Save
            @if ($requestInProgress()) {
              <mat-spinner class="ml-4" [diameter]="20" />
            }
          </wcg-button>
        </div>
      </wcg-dialog>

      <ng-template
        #selectRef
        let-formControl="formControl"
        let-options="options"
        let-props="props"
      >
        <div>
          <wcg-uni-select
            class="submission-type-dialog__select"
            [formControl]="formControl"
            [options]="options"
            [label]="props?.label || ''"
            [required]="props?.required || false"
            [height]="$inputHeight().Comfortable"
            [errorText]="props.errorText"
          />
        </div>
      </ng-template>
    </form>
  `,
})
export class SDocumentTypeDialogComponent implements OnInit {
  public readonly data = inject<AddDocumentTypeDialogData>(MAT_DIALOG_DATA);
  private readonly _dialogRef = inject(
    MatDialogRef<SDocumentTypeDialogComponent>,
  );
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _formBuilder = inject(FormBuilder);

  size = DialogSize;
  documentTypeDialogForm = this._createForm();

  $inputHeight = signal<typeof WcgInputHeight>(WcgInputHeight);
  $currentValidationError = signal<string>('');
  $documentFormGroup = signal<DocumentTypesFormArray>(
    {} as DocumentTypesFormArray,
  );

  $documentTypesList: Signal<WcgMenuOption[]> = computed(() =>
    this.data.$documentTypes(),
  );
  $attributesList: Signal<WcgMenuOption[]> = computed(() =>
    this.data.$attributes(),
  );

  $title = computed(() => {
    return this.data.$mode() === 'Create'
      ? `Add Document Type`
      : `Edit Document Type`;
  });

  $requestInProgress = computed(() => this.data.$isLoading());

  $errorText = signal<string>('');

  constructor() {
    this._customListeners();
  }

  ngOnInit(): void {
    if (this.data.$mode() === 'Edit') {
      this._patchDialogTypeForm();
    }
  }

  save(): void {
    if (this.$requestInProgress()) return;
    const _data = this.documentTypeDialogForm.getRawValue();
    this.data.callbacks.save({
      action: this.data.$mode() === 'Edit' ? 'update' : 'create',
      attribute_type_id: _data.attribute!.id!,
      document_type_id: _data.documentType!.id!,
    });
  }

  cancel(): void {
    this._dialogRef.close();
  }

  /**
   * @Creating Form
   * **/
  private _createForm() {
    return this._formBuilder.group({
      attribute: new FormControl<WcgMenuOption | null>(null, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      documentType: new FormControl<WcgMenuOption | null>(null, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
  }

  private _patchDialogTypeForm() {
    const _documentType = this.data.$documentType();
    const _constructWcgMenuOption = (id: string, label: string) => ({
      id,
      label,
    });
    this.documentTypeDialogForm.patchValue({
      attribute: {
        ..._constructWcgMenuOption(
          _documentType.attribute_type_id,
          _documentType.attribute_type_name,
        ),
      },
      documentType: {
        ..._constructWcgMenuOption(_documentType.id, _documentType.name),
      },
    });
  }
  /*
   * @Custom Listeners
   * */
  private _customListeners(): void {
    toObservable(this.data.$canCloseDialog)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (canClose) => {
          if (canClose) {
            this._dialogRef.close();
          }
        },
      });

    this.documentTypeDialogForm.controls.documentType?.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (value) => {
          if (value) {
            const _duplicates = this.data
              .$sDocumentTypes()
              .filter((d) => d.id === value.id);
            if (_duplicates.length > 0) {
              this.documentTypeDialogForm.controls.documentType.setErrors({
                error: 'Document Type Already Exists!',
              });
              this.$errorText.set('Document Type Already Exists!');
              this.documentTypeDialogForm.controls.documentType.updateValueAndValidity(
                { emitEvent: false },
              );
            }
          }
        },
      });
  }
  /**
   * @Custom ListenersEnd
   * */
}
